/**
 * Costanti condivise della dashboard ESXi ("Ambienti di test").
 *
 * Stati, azioni ed etichette derivano dal contratto backend documentato in
 * backend/docs/esxi-api.md. Non introdurre stati, azioni o campi non presenti
 * nel contratto: qualunque valore non riconosciuto viene degradato a UNKNOWN /
 * "non determinato" senza generare falsi allarmi.
 */

// --- Stati di alimentazione VM ----------------------------------------------
// Valori documentati per `powerState`: ON, OFF, SUSPENDED. UNKNOWN è un
// fallback puramente lato UI per dati mancanti o non previsti.
export const POWER_STATE = {
  ON: 'ON',
  OFF: 'OFF',
  SUSPENDED: 'SUSPENDED',
  UNKNOWN: 'UNKNOWN',
};

/** Normalizza un powerState grezzo in uno dei valori noti (UNKNOWN se fuori scala). */
export const normalizePowerState = (raw) => {
  const v = String(raw || '').toUpperCase();
  return POWER_STATE[v] || POWER_STATE.UNKNOWN;
};

/**
 * Metadati di presentazione per lo stato di alimentazione.
 * Palette coerente con la richiesta: verde ON, grigio OFF, arancione
 * transizione/warning, blu info. Non ci si affida al solo colore: label + icona.
 */
export const POWER_STATE_META = {
  ON: {
    label: 'Accesa',
    dot: 'bg-emerald-500',
    badge:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    icon: 'play',
  },
  OFF: {
    label: 'Spenta',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    icon: 'power',
  },
  SUSPENDED: {
    label: 'Sospesa',
    dot: 'bg-amber-500',
    badge:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    icon: 'pause',
  },
  UNKNOWN: {
    label: 'Sconosciuto',
    dot: 'bg-gray-300',
    badge: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    icon: 'question',
  },
};

export const powerStateMeta = (state) =>
  POWER_STATE_META[normalizePowerState(state)] || POWER_STATE_META.UNKNOWN;

// --- Azioni ammesse ----------------------------------------------------------
// Il contratto accetta esclusivamente "powerOn" e "shutdown".
export const ACTION = {
  POWER_ON: 'powerOn',
  SHUTDOWN: 'shutdown',
};

export const ACTION_META = {
  powerOn: {
    label: 'Accendi',
    verb: 'Accensione',
    // Azione non distruttiva → blu.
    style: 'bg-sky-600 text-white hover:bg-sky-700',
    icon: 'play',
    targetLabel: 'Accesa',
  },
  shutdown: {
    // Etichetta volutamente "corretta", mai "forzato".
    label: 'Spegni correttamente',
    verb: 'Spegnimento controllato',
    style: 'bg-amber-600 text-white hover:bg-amber-700',
    icon: 'power',
    targetLabel: 'Spenta',
  },
};

export const actionMeta = (action) => ACTION_META[action] || null;

// Solo le azioni realmente supportate sono considerate valide, anche se il
// backend dovesse restituire per errore un valore non previsto.
export const isSupportedAction = (action) =>
  action === ACTION.POWER_ON || action === ACTION.SHUTDOWN;

// --- Stati operazione asincrona ---------------------------------------------
export const OPERATION_STATUS = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  VERIFYING: 'VERIFYING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT',
};

// Stati che chiudono il polling.
export const TERMINAL_OPERATION_STATUSES = new Set([
  OPERATION_STATUS.SUCCEEDED,
  OPERATION_STATUS.FAILED,
  OPERATION_STATUS.TIMED_OUT,
]);

export const isTerminalOperation = (status) =>
  TERMINAL_OPERATION_STATUSES.has(status);

export const isOperationSuccess = (status) =>
  status === OPERATION_STATUS.SUCCEEDED;

export const isOperationFailure = (status) =>
  status === OPERATION_STATUS.FAILED || status === OPERATION_STATUS.TIMED_OUT;

export const OPERATION_STATUS_META = {
  QUEUED: {
    label: 'In coda',
    style: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    tone: 'info',
  },
  RUNNING: {
    label: 'In esecuzione',
    style: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    tone: 'info',
  },
  VERIFYING: {
    label: 'Verifica stato',
    style: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    tone: 'info',
  },
  SUCCEEDED: {
    label: 'Completata',
    style:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    tone: 'success',
  },
  FAILED: {
    label: 'Fallita',
    style: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
    tone: 'error',
  },
  TIMED_OUT: {
    label: 'Timeout',
    style:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
    tone: 'warning',
  },
};

export const operationStatusMeta = (status) =>
  OPERATION_STATUS_META[status] || {
    label: status || '—',
    style: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    tone: 'info',
  };

// Intervallo di polling moderato richiesto dal contratto (ms).
export const OPERATION_POLL_INTERVAL_MS = 2000;

// --- Identità VM -------------------------------------------------------------
// Il catalogo correla per BIOS UUID; l'unico stato che consideriamo
// pienamente affidabile è MATCHED. Ogni altro valore, o la presenza della VM
// tra gli avvisi di catalogo, la rende "ambigua": in quel caso le azioni
// operative NON vengono mostrate.
export const IDENTITY_STATUS_META = {
  MATCHED: {
    ambiguous: false,
    label: 'Identità verificata',
    style:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  NAME_MISMATCH: {
    ambiguous: true,
    label: 'Nome non coerente',
    style:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  },
  DUPLICATE_UUID: {
    ambiguous: true,
    label: 'UUID duplicato',
    style: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  },
  UNCATALOGUED: {
    ambiguous: true,
    label: 'Non catalogata',
    style:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  },
  MISSING: {
    ambiguous: true,
    label: 'Catalogata ma assente',
    style:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  },
  UNKNOWN: {
    ambiguous: true,
    label: 'Identità non determinata',
    style: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
};

export const identityStatusMeta = (status) => {
  const key = String(status || 'UNKNOWN').toUpperCase();
  return IDENTITY_STATUS_META[key] || IDENTITY_STATUS_META.UNKNOWN;
};

// --- Ambienti ----------------------------------------------------------------
// Ordine di visualizzazione suggerito. Ambienti non elencati vengono mostrati
// dopo, in ordine alfabetico; le VM senza ambiente finiscono in coda.
export const ENVIRONMENT_ORDER = [
  'SVILUPPO',
  'TIE',
  'CCT',
  'SAPRA',
  'BACKUP',
];

export const NO_ENVIRONMENT_LABEL = 'Senza ambiente';
export const NO_PRODUCT_LABEL = 'Senza prodotto';

// --- Modalità di visualizzazione --------------------------------------------
export const VIEW_MODE = { CARDS: 'cards', TABLE: 'table' };
export const VIEW_MODE_STORAGE_KEY = 'esxi:viewMode';

// --- Heartbeat / VMware Tools ------------------------------------------------
export const HEARTBEAT_META = {
  green: { label: 'Verde', dot: 'bg-emerald-500' },
  yellow: { label: 'Giallo', dot: 'bg-amber-500' },
  red: { label: 'Rosso', dot: 'bg-red-500' },
  gray: { label: 'Non determinato', dot: 'bg-gray-300' },
};

export const heartbeatMeta = (status) =>
  HEARTBEAT_META[String(status || 'gray').toLowerCase()] || HEARTBEAT_META.gray;
